import Swiper, { Navigation, Scrollbar, SwiperOptions, Mousewheel } from "swiper";

class ItemCarousel extends HTMLElement {
  slider?: HTMLElement | null;
  swiper?: Swiper;
  breakpoint: number = 990;
  disabled_mobile?: boolean;
  disabled_desktop?: boolean;
  swiper_params: SwiperOptions = {};
  transparent_btns?: boolean;
  scrollbar_mobile?: boolean;
  scrollbar_desktop?: boolean;
  small_btns?: boolean;
  columns_mobile?: number;
  columns_desktop?: number;
  original_class?: string;
  nextBtn?: HTMLElement;
  prevBtn?: HTMLElement;

  static observedAttributes = [
    "columns-desktop",
    "columns-mobile",
    "disabled-mobile",
    "disabled-desktop",
    "transparent-btns",
    "transparent-arrows",
    "scrollbar-mobile",
    "scrollbar-desktop",
    "small-btns",
  ];

  connectedCallback() {
    this.slider = this.querySelector(".item-carousel");
    this.disabled_mobile = this.getAttribute("disabled-mobile") === "true";
    this.disabled_desktop = this.getAttribute("disabled-desktop") === "true";
    this.transparent_btns = this.getAttribute("transparent-btns") === "true";
    this.scrollbar_mobile = this.getAttribute("scrollbar-mobile") === "true";
    this.small_btns = this.getAttribute("small-btns") === "true";
    this.scrollbar_desktop = this.getAttribute("scrollbar-desktop")
      ? this.getAttribute("scrollbar-desktop") === "true"
      : true;
    this.columns_desktop = Number(this.getAttribute("columns-desktop")) || 4;
    this.columns_mobile = parseInt(this.getAttribute("columns-mobile")) || 2;
    this.nextBtn = document.createElement("button");
    this.prevBtn = document.createElement("button");
    this.id = `item-carousel-${Math.floor(Math.random() * 1000000000)}`;

    this.original_class = this.slider?.className;

    this.swiper_params = {
      loop: false,
      modules: [Navigation, Scrollbar, Mousewheel],
      speed: 400,
      mousewheel: {
        forceToAxis: true,
        thresholdDelta: 10,
      },
      resistanceRatio: 0.5,
      navigation: {
        nextEl: `#${this.id} .next-btn`,
        prevEl: `#${this.id} .prev-btn`,
      },
      scrollbar: {
        el: `#${this.id} .swiper-scrollbar`,
        draggable: true,
      },
      spaceBetween: 8,
      slidesPerView: this.columns_mobile,
      breakpoints: {
        [this.breakpoint]: {
          slidesPerView: this.columns_desktop,
        },
      },
    };

    window.addEventListener("resize", this.handleResize.bind(this));

    if (window.innerWidth < this.breakpoint && this.disabled_mobile) return;
    if (window.innerWidth >= this.breakpoint && this.disabled_desktop) return;

    this.handleResize();
  }

  calculateOffset() {
    return this.slider ? -(this.getBoundingClientRect().width - this.slider.getBoundingClientRect()?.width) : 0;
  }

  handleResize() {
    // re-adjust right offset
    if (this.swiper?.params?.slidesOffsetAfter) {
      this.swiper_params.slidesOffsetAfter = this.calculateOffset();
      this.swiper.params.slidesOffsetAfter = this.swiper_params.slidesOffsetAfter;
    }

    const shouldDestroy =
      this.swiper &&
      ((window.innerWidth >= this.breakpoint && this.disabled_desktop) ||
        (window.innerWidth < this.breakpoint && this.disabled_mobile));

    const shouldBuild =
      (this.swiper?.destroyed || !this.swiper) &&
      ((window.innerWidth >= this.breakpoint && !this.disabled_desktop) ||
        (window.innerWidth < this.breakpoint && !this.disabled_mobile));

    if (shouldDestroy) this.destroySlider();
    else if (shouldBuild) this.buildSlider();

    this.setBtnTop();
  }

  // Destroy the slider and rebuild the grid view
  destroySlider() {
    if (!this.slider || !this.swiper) return;

    this.swiper.destroy();

    const ogSlides = this.slider.querySelector(".swiper-wrapper")?.children;
    if (!ogSlides) return;

    const slides = Array.from(ogSlides)?.map((slide) => slide.cloneNode(true));

    this.slider.className = this.original_class ?? "";
    this.slider.innerHTML = "";

    for (const slide of slides) {
      this.slider.appendChild(slide);
    }
  }

  buildSlider() {
    if (!this.slider) return;

    const wrapper = document.createElement("div");
    const scrollBar = document.createElement("div");
    const slides = this.slider.children;

    if (slides.length <= 0) return;

    this.slider.classList.add("swiper");
    wrapper.classList.add("swiper-wrapper");

    scrollBar.classList.add("swiper-scrollbar");
    if (!this.scrollbar_mobile) {
      scrollBar.classList.add("hide-mobile");
    }
    if (!this.scrollbar_desktop) {
      scrollBar.classList.add("hide-desktop");
    }

    this.nextBtn.classList.add("next-btn", "button", "button--carousel", "button--white", "hide-mobile");
    this.nextBtn.setAttribute("aria-label", "Next Slide");
    this.nextBtn.innerHTML = `<span><svg><use xlink:href="#icon-chevron-right-small"></use></svg></span>`;
    this.prevBtn.classList.add("prev-btn", "button", "button--carousel", "button--white", "hide-mobile");
    this.prevBtn.setAttribute("aria-label", "Previous Slide");
    this.prevBtn.innerHTML = `<span><svg><use xlink:href="#icon-chevron-left-small"></use></svg></span>`;

    this.setBtnTop();

    if (this.small_btns) {
      this.nextBtn.classList.add("button--small");
      this.prevBtn.classList.add("button--small");
    }

    if (this.transparent_btns) {
      this.nextBtn.classList.add("button--transparent");
      this.prevBtn.classList.add("button--transparent");
    }

    for (const slide of slides) {
      slide.classList.add("swiper-slide");
      wrapper.appendChild(slide.cloneNode(true));
    }

    this.slider.innerHTML = "";
    this.slider.appendChild(wrapper);
    this.slider.appendChild(this.prevBtn);
    this.slider.appendChild(this.nextBtn);
    this.slider.appendChild(scrollBar);

    this.swiper_params.slidesOffsetAfter = this.calculateOffset();

    this.swiper = new Swiper(this.slider, this.swiper_params);
  }

  setBtnTop() {
    setTimeout(() => {
      const imageHeight = this.querySelector(".carousel-item img")?.getBoundingClientRect()?.height;

      if (imageHeight) {
        this.prevBtn.style.top = `${Math.floor(imageHeight / 2)}px`;
        this.nextBtn.style.top = `${Math.floor(imageHeight / 2)}px`;
      }
    }, 200);
  }
}

customElements.define("item-carousel", ItemCarousel);
